import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchLanguages } from '../../../../../redux/actions/user';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        inputContainer: {
            marginBottom: '40px'
        },
        inputFields: {
            background: 'transparent',
            border: '0',
            borderRadius: '6px',
            color: '#1f1f51',
            fontFamily: 'Manrope',
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '-0.21px',
            padding: '10px 0px 6px 0px',
            resize: 'none',
            width: '100%',
            '&:placeholder-shown': {
                fontStyle: 'italic'
            },
            '&:focus': {
                outline: 'none !important'
            }
        },
        saveInputBtnContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            paddingBottom: '15px',
            '& button': {
                cursor: 'pointer',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontWeight: '800'
            }
        },
        saveBtnDisabled: {
            opacity: '50%',
            '& button': {
                cursor: 'auto'
            }
        },
        skipStateContainer: {
            display: 'flex',
            flexDirection: 'row'
        },
        skipStateInput: {
            fontFamily: 'Manrope',
            fontSize: '1.75rem',
            fontWeight: 700
        },
        editIconContainer: {
            cursor: 'pointer',
            height: 'auto',
            margin: '0 0 0 15px',
            width: '1.5rem'
        },
        editIcon: {
            height: 'inherit',
            width: 'inherit'
        },
        editUnderline: {
            backgroundColor: '#E3E4FA',
            border: '3px solid #E3E4FA',
            borderRadius: '10px',
            height: '0'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            color: '#1f1f51',
            fontFamily: 'Manrope',
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '-0.21px'
        },
        orgLogo: {
            borderRadius: '50%',
            height: '32px',
            marginRight: '12px',
            width: '32px'
        },
        orgContainer: {
            display: 'flex',
            paddingTop: '3px'
        },
        optionalLabel: {
            color: '#7A4406',
            fontSize: '0.8rem',
            fontWeight: 100,
            letterSpacing: '0.15em',
            marginLeft: '8px',
            opacity: 0.7
        },
        requiredLabel: {
            color: '#A80A2D',
            fontSize: '0.8rem',
            fontWeight: 100,
            letterSpacing: '0.15em',
            marginLeft: '8px',
            opacity: 0.7
        }
    })
);

type LanguagePreferenceInputProps = {
    intakeInput: string;
    inputTitleClass: (input: string) => string;
    setIntakeInput: (input: string) => void;
};

const LanguagePreferenceInput: React.FC<LanguagePreferenceInputProps> = ({
    intakeInput,
    inputTitleClass,
    setIntakeInput
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const languages = useSelector((state: any) => state.user.languages);

    useEffect(() => {
        dispatch(fetchLanguages());
    }, [dispatch]);

    const [open, setOpen] = useState<boolean>(false);
    const [input, setInput] = useState<string>('');

    const handleChangeLang = (e: React.ChangeEvent<{ value: unknown }>) => {
        const value = e.target.value as string;
        setInput(value);
        setIntakeInput(value);
    };

    const handleInputClick = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        setOpen(!open);

        if ((e.target as HTMLSelectElement).value === undefined) {
            return;
        }
    };

    return (
        <div className={classes.inputContainer}>
            <span className={inputTitleClass(intakeInput)}>
                Member's Language Preference
                <span
                    className={
                        classes.optionalLabel
                    }
                >
                    {`(OPTIONAL)`}
                </span>
            </span>
           <Select
                className={classes.inputFields}
                disableUnderline
                displayEmpty
                onChange={(e) => handleChangeLang(e as any)}
                onClick={(e) => handleInputClick(e)}
                open={open}
                value={input}
                variant="standard"
                inputProps={{
                    'aria-label': "Member's Language Preference - Select preferred language"
                }}
            >
                <MenuItem disabled value="">
                    <span
                        className={classes.menuItem}
                        style={{ fontStyle: 'italic', opacity: '0.6' }}
                    >
                        Click to select
                    </span>
                </MenuItem>
                {languages.length > 0
                    ? languages.map((language: string, index: number) => {
                            return (
                                <MenuItem
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    value={language}
                                >
                                    <span className={classes.menuItem}>
                                        {language.charAt(0).toUpperCase() + language.slice(1)}
                                    </span>
                                </MenuItem>
                            );
                        })
                    : null}
            </Select>
        </div>
    );
};

export default LanguagePreferenceInput;
